import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { isNull } from 'lodash-es';
import { AgGridReact } from 'ag-grid-react';
import { ICellRendererParams, SelectionChangedEvent } from 'ag-grid-community';
import { GridChartsModule } from '@ag-grid-enterprise/charts';
import { SetFilterModule } from "@ag-grid-enterprise/set-filter";

import { BodySmall, Box, Button, Info, Snackbar, TableHeaderMenuIcon, ToolbarType } from '@parspec/pixel';

import AddContactDialog from '../AddContactDialog';
import CustomToolBarPanel from '../../../shared/CustomToolBarPanel';
import DeleteConfirmationDialog from '../../../shared/DeleteConfirmationDialog';
import { LoadingOverlayComponent, NoRowsOverlayComponent } from '../../../BOM/shared/commonTemplates';

import { defaultColDef, LOCAL_TABLE_FIELDS } from '../../shared/constant';
import { addOutsideSalesField, dateComparator, emailTemplate, getCombinedName, getLastModifiedFilterText, menuOptions, renderCompanyName, renderContactName, renderLastModifiedDate, uniqueByLatestVal } from '../../shared/utils';
import { deleteTitle, contactDeleteBody, getRowId, Company, FORM_FIELDS } from '../utils';
import { CONTACT_CREATED_MSG, CONTACT_DELETED_MSG, EMAIL_COPIED_MSG } from '../../../shared/constants';
import { useDeleteContactsMutation, useGetContactsQuery } from '../queries';
import { endpoints } from '../queries/apis';
import { BulkEditContactRequest, Contact, ContactWithSalesAgent } from '../queries/apiTypes';
import { useGetUserProfileInfoQuery } from '../../../Settings/MyProfile/queries';
import { useSnackbar } from '../../../shared/hooks/useSnackbar';
import { CustomTableHeader } from 'src/features/shared/CustomTableHeader';
import ImportContactFlow from '../../shared/ImportContactFlow';
import BulkEditContactModal from '../../shared/BulkEditModals/BulkEditContact';
import type { ImportFileResponse } from '../../Company/queries/apiTypes';
import { useActiveUsers } from '../../shared/hooks/useActiveUsers';
import { stringComparator } from 'src/features/Dashboard/shared/utils';

interface IContactTableProps {
    isAssociatedContactTable?: boolean;
    associatedTableData?: Contact[];
    preSelectedCompany?: Company;
}

const sortContacts = (contacts: Array<ContactWithSalesAgent>) => {
    return contacts.sort((a, b) => stringComparator(
        getCombinedName(a.first_name, a.last_name),
        getCombinedName(b.first_name, b.last_name),
    ));
}

const ContactsTable = ({ isAssociatedContactTable = false, associatedTableData = [], preSelectedCompany }: IContactTableProps) => {
    const [tableData, setTableData] = useState<ContactWithSalesAgent[]>();
    const tableContainerRef = useRef<any>(null);
    const [tableHeight, setTableHeight] = useState<number>();
    const [selectedRows, setSelectedRows] = useState<ContactWithSalesAgent[]>([]);
    const [searchText, setSearchText] = useState('');
    const [openAddEditDialog, setOpenAddEditDialog] = useState(false);
    const [openBulkEditDialog, setOpenBulkEditDialog] = useState(false);
    const [openConfirmationDialog, setOpenConfirmationDialog] = useState(false);
    const [openImportDialog, setOpenImportDialog] = useState(false);
    const [deleteIds, setDeleteIds] = useState<number[]>([]);
    const [apiUrl, setApiUrl] = useState(endpoints.contacts);
    const [isAnyFilterApplied, setIsAnyFilterApplied] = useState(false);

    const gridRef = useRef<any>(null);
    const timeoutRef = useRef<NodeJS.Timeout>();

    const navigate = useNavigate();

    const { snackbarInfo, setSnackbarOpen, setSnackbarClose } = useSnackbar();

    const { data: contactsData, isFetching } = useGetContactsQuery(apiUrl);
    const { data: userProfile, isLoading: isAdminLoading } = useGetUserProfileInfoQuery();
    const { mutateAsync: deleteContacts, isLoading: deleteLoading } = useDeleteContactsMutation();
    const {
        dataMap: activeUsersMap,
        isLoading: isLoadingActiveUsers,
    } = useActiveUsers(true);

    const resetApiQueryInstance = useCallback(() => {
        // reset url to initial after mutations which invalidates contacts query and happen within this component's lifecycle.
        setApiUrl(endpoints.contacts);
    }, []);

    const isAdmin = userProfile?.data?.role === 'admin';
    const loadingFlag = isFetching || isAdminLoading || isLoadingActiveUsers;

    useEffect(() => () => {
        clearTimeout(timeoutRef.current);
    }, []);

    useEffect(() => {
        if (!isAssociatedContactTable && contactsData?.data && !loadingFlag) {
            const { results: vanillaResults, next } = contactsData.data;
            const results = addOutsideSalesField<Contact>(vanillaResults, activeUsersMap);

            if (next) {
                setApiUrl(next);
            }
            if (tableData && tableData.length > 0) {
                const arr = uniqueByLatestVal([...tableData, ...results]);
                setTableData(sortContacts(arr));
            } else {
                setTableData(sortContacts(results));
            }
        }
    }, [apiUrl, loadingFlag]);

    useEffect(() => {
        if (isAssociatedContactTable && associatedTableData) {
            setTableData(addOutsideSalesField<Contact>(associatedTableData, activeUsersMap));
        }
    }, [associatedTableData]);

    useEffect(() => {
        if (tableContainerRef?.current?.offsetHeight) {
            setTableHeight(tableContainerRef?.current?.offsetHeight);
        }
    }, [tableContainerRef]);

    //this useEffect handles the loader in table
    useEffect(() => {
        if (!tableData && loadingFlag) {
            gridRef?.current?.api?.showLoadingOverlay();
        } else if (tableData && tableData?.length === 0) {
            setTimeout(() => {
                gridRef?.current?.api?.showNoRowsOverlay();
            }, 0);
        } else {
            gridRef?.current?.api?.hideOverlay();
        }
    }, [tableData]);

    useEffect(() => {
        return () => {
            setTableData(undefined);
            setTableHeight(undefined);
        };
    }, []);

    const toggleAddEditDialog = useCallback(() => setOpenAddEditDialog((old) => !old), []);

    // to open the create popup if user comes by clicking Add New Contact
    useEffect(() => {
        if (window?.openContactPopup) {
            toggleAddEditDialog();
            delete window.openContactPopup;
        }
    }, []);

    const onCheckboxChange = (args: SelectionChangedEvent<ContactWithSalesAgent>) => {
        setSelectedRows(args.api.getSelectedRows());
    };

    const handleClearSelection = () => {
        gridRef.current?.api?.deselectAll();
    };

    const onClickEdit = (props: ICellRendererParams) => {
        const { id } = props.data;
        navigate(`/v2/company/1/contacts/${id}`);
    };

    const onClickDelete = (props: ICellRendererParams) => {
        setDeleteIds([props?.data?.id]);
        setOpenConfirmationDialog(true);
    };

    const closeConfirmationDialog = () => {
        setOpenConfirmationDialog(false);
    };

    const onDeleteBulk = async (clearSelection?: boolean) => {
        await deleteContacts(deleteIds);
        setTableData((old: Contact[] | undefined) => old?.filter((item) => !deleteIds.includes(item.id)));
        setDeleteIds([]);
        closeConfirmationDialog();
        setSnackbarOpen(CONTACT_DELETED_MSG);
        if (clearSelection) handleClearSelection();
    };

    const onClickMultipleDelete = () => {
        const ids = selectedRows.map((item: Contact) => item.id);
        setDeleteIds(ids);
        setOpenConfirmationDialog(true);
    };

    const renderEmail = (args: ICellRendererParams) => {
        const { email } = args.data;
        const handleEmailClick = (event: React.MouseEvent) => {
            event.preventDefault();
            navigator.clipboard.writeText(email);
            setSnackbarOpen(EMAIL_COPIED_MSG);
        };
        return (
            <Box display="flex" alignItems="center" height="100%">
                {emailTemplate(email, handleEmailClick)}
            </Box>
        );
    };

    const columnDefs = useMemo<any>(() => {
        return [
            {
                field: 'id',
                hide: true
            },
            { headerCheckboxSelection: true, checkboxSelection: true, minWidth: 50, width: 50, maxWidth: 50, resizable: true, hide: !isAdmin },
            {
                field: 'first_name',
                headerName: 'Name',
                minWidth: 145,
                cellRenderer: (args: ICellRendererParams) => renderContactName(args),
                suppressMenu: true,
                valueGetter: (params: any) => getCombinedName(params?.data?.first_name, params?.data?.last_name),
                comparator: (valueA: string, valueB: string) => stringComparator(valueA, valueB),
            },
            {
                field: 'crm_id',
                headerName: 'CRM ID',
                minWidth: 145,
                valueFormatter: (params: ICellRendererParams) => {
                    if (!params.value) return '--';

                    return params.value;
                }
            },
            {
                field: LOCAL_TABLE_FIELDS.OUTSIDE_SALES_AGENT_NAME,
                headerName: "Outside Sales",
                minWidth: 170,
                suppressMenu: false,
                filter: true,
                valueFormatter: (params: ICellRendererParams) => {
                    if (!params.value) return '--';
                    return params.value;
                },
                comparator: (valueA: string, valueB: string) => stringComparator(valueA, valueB),
                headerComponent: CustomTableHeader,
                headerComponentParams: {
                    enableFiltering: true
                }
            },
            {
                field: FORM_FIELDS.ROLE,
                headerName: 'Job Title',
                minWidth: 150,
                filter: 'agSetColumnFilter',
                suppressMenu: false,
                valueFormatter: (params: ICellRendererParams) => {
                    if (!params.value) return '--';

                    return params.value;
                },
                headerComponent: CustomTableHeader,
                headerComponentParams: {
                    enableFiltering: true
                }
            },
            {
                field: FORM_FIELDS.EMAIL,
                headerName: 'Email',
                minWidth: 170,
                cellRenderer: renderEmail
            },
            {
                field: FORM_FIELDS.PHONE,
                headerName: 'Phone Number',
                minWidth: 165,
                valueFormatter: (params: ICellRendererParams) => {
                    if (!params.value) return '--';

                    return params.value;
                }
            },
            {
                field: 'company_info.name',
                headerName: 'Company',
                minWidth: 145,
                cellRenderer: (args: ICellRendererParams) => renderCompanyName(args),
                suppressMenu: false,
                filter: 'agSetColumnFilter',
                hide: isAssociatedContactTable,
                headerComponent: CustomTableHeader,
                headerComponentParams: {
                    enableFiltering: true
                }
            },
            {
                field: 'company_info.business_type',
                headerName: 'Business Type',
                minWidth: 165,
                suppressMenu: false,
                filter: 'agSetColumnFilter',
                hide: isAssociatedContactTable,
                valueFormatter: (params: ICellRendererParams) => {
                    if (!params.value) return '--';

                    return params.value;
                },
                headerComponent: CustomTableHeader,
                headerComponentParams: {
                    enableFiltering: true
                }
            },
            {
                field: FORM_FIELDS.ADDRESS_LINE_1,
                headerName: 'Address Line 1',
                minWidth: 165,
                valueFormatter: (params: ICellRendererParams) => {
                    if (!params.value) return '--';

                    return params.value;
                }
            },
            {
                field: FORM_FIELDS.ADDRESS_LINE_2,
                headerName: 'Address Line 2',
                minWidth: 165,
                valueFormatter: (params: ICellRendererParams) => {
                    if (!params.value) return '--';

                    return params.value;
                }
            },
            {
                field: FORM_FIELDS.COUNTRY,
                headerName: 'Country',
                minWidth: 125,
                suppressMenu: false,
                filter: 'agSetColumnFilter',
                valueFormatter: (params: ICellRendererParams) => {
                    if (!params.value) return '--';

                    return params.value;
                },
                headerComponent: CustomTableHeader,
                headerComponentParams: {
                    enableFiltering: true
                }
            },
            {
                field: FORM_FIELDS.CITY,
                headerName: 'City',
                minWidth: 140,
                filter: 'agSetColumnFilter',
                valueFormatter: (params: ICellRendererParams) => {
                    if (!params.value) return '--';

                    return params.value;
                },
                headerComponent: CustomTableHeader,
                headerComponentParams: {
                    enableFiltering: true
                }
            },
            {
                field: FORM_FIELDS.STATE,
                headerName: 'State',
                minWidth: 125,
                suppressMenu: false,
                filter: 'agSetColumnFilter',
                valueFormatter: (params: ICellRendererParams) => {
                    if (!params.value) return '--';

                    return params.value;
                },
                headerComponent: CustomTableHeader,
                headerComponentParams: {
                    enableFiltering: true
                }
            },
            {
                field: FORM_FIELDS.ZIP,
                headerName: 'Zip',
                minWidth: 100,
                valueFormatter: (params: ICellRendererParams) => {
                    if (!params.value) return '--';

                    return params.value;
                }
            },

            {
                field: 'lastModified',
                headerName: 'Last Modified',
                minWidth: 180,
                cellRenderer: renderLastModifiedDate,
                suppressMenu: true,
                getQuickFilterText: getLastModifiedFilterText,
                comparator: dateComparator
            },
            {
                headerComponent: () => (
                    <Box mt={1}>
                        <TableHeaderMenuIcon color="secondary" fontSize="medium" />
                    </Box>
                ),
                pinned: 'right',
                minWidth: 50,
                maxWidth: 50,
                resizable: false,
                cellRenderer: (props: ICellRendererParams) => menuOptions(props, onClickEdit, onClickDelete, isAdmin)
            }
        ];
    }, [isAdmin]);

    const clearAllFilters = () => {
        gridRef.current.api.setFilterModel(null);
    };

    const handleAddContactSuccess = useCallback(() => {
        resetApiQueryInstance();
        setSnackbarOpen(CONTACT_CREATED_MSG);
    }, [resetApiQueryInstance, setSnackbarOpen]);

    const onCloseBanner = () => {
        setSelectedRows([]);
        handleClearSelection();
    };

    const closeBulkEditDialog = () => setOpenBulkEditDialog(false);
    const closeDialog = useCallback(() => {
        setOpenImportDialog(false);
    }, []);

    // onUpload success, backend could return us sometimes non-blocking error, even when all imports
    // have been successfull. Eg: All rows are imported, but issue in importing 1 certain column
    // so backend doesn't prevent whole import. In that case we need to show both. Initial success
    // message of whole import being successfull, and then error of partial non-blocking error.
    const onUploadSuccess = (uploadResponse: ImportFileResponse) => {
        const { message, error } = uploadResponse;
        closeDialog();
        setSnackbarOpen(message, 1500);
        resetApiQueryInstance();

        if (error) {
            // clear any previous pending timeout, before setting new
            clearTimeout(timeoutRef.current);
            timeoutRef.current = setTimeout(() => setSnackbarOpen(error, 3000), 2000);
        }
    };

    const handleBulkEditSuccess = (message: string, updatedData: BulkEditContactRequest) => {
        setSnackbarOpen(message);
        closeBulkEditDialog();
        if (!updatedData.outside_sales_agent) return;
        const updatedRowIds = new Set(updatedData.contact_ids);

        // locally update table data
        setTableData(prev => prev?.map(contact => {
            if (updatedRowIds.has(contact.id)) {

                return {
                    ...contact,
                    outside_sales_agent: updatedData.outside_sales_agent,
                    outside_sales_agent_name: !isNull(updatedData.outside_sales_agent)
                        ? activeUsersMap.get(updatedData.outside_sales_agent)?.user_details.name
                        : "",
                }
            }
            return contact;
        }));
        handleClearSelection();
    };

    const toolbarRightSection = useMemo(() => {
        return (
            <Box display="flex" gap={2}>
                {isAdmin && !isAssociatedContactTable && (
                    <Button variant="outlined" color="secondary" onClick={() => setOpenImportDialog(true)}>
                        Import
                    </Button>
                )}
                <Button variant="contained" onClick={toggleAddEditDialog}>
                    Create New
                </Button>
            </Box>
        );
    }, [isAdmin, isAssociatedContactTable, toggleAddEditDialog]);

    const toolBarOptions = useMemo(() => {
        return isAdmin ? (['search', 'clearFilters', 'selectedItems', 'edit', 'delete'] as ToolbarType) : (['search', 'clearFilters'] as ToolbarType);
    }, [isAdmin]);

    const handleSnackbarClose = (_e: Event | React.SyntheticEvent<any, Event>, reason: string) => {
        if (reason === 'clickaway') {
            return;
        }
        setSnackbarClose();
    };

    function onFilterChanged() {
        const allFilters = gridRef.current?.api?.getFilterModel();
        setIsAnyFilterApplied(Object.keys(allFilters).length > 0);
    }

    return (
        <>
            <Snackbar open={snackbarInfo.open} onClose={handleSnackbarClose} message={snackbarInfo.message} autoHideDuration={snackbarInfo.autoHideDuration} />
            <Box zIndex={1} width={'100%'} position={'relative'} p={4} pb={2} flex={1} ref={tableContainerRef}>
                {tableHeight ? (
                    <Box display="flex" flexDirection="column" height={isAssociatedContactTable ? '350px' : 1}>
                        <CustomToolBarPanel
                            toolBarOptions={toolBarOptions}
                            selectedRowCount={selectedRows.length}
                            disabledToolBarButton={selectedRows.length === 0}
                            onEdit={() => setOpenBulkEditDialog(true)}
                            onTextSearch={(value) => setSearchText(value)}
                            toolbarRightSection={toolbarRightSection}
                            onDelete={onClickMultipleDelete}
                            onClearFilter={clearAllFilters}
                            onCloseBanner={onCloseBanner}
                            searchText={searchText}
                            isFilterApplied={isAnyFilterApplied}
                        />
                        <Box height={isAssociatedContactTable ? 300 : tableHeight - 120} className="ag-theme-quartz" mt={2} flex={1}>
                            <AgGridReact
                                ref={gridRef}
                                rowData={tableData}
                                getRowId={getRowId}
                                columnDefs={columnDefs}
                                defaultColDef={defaultColDef}
                                suppressMovableColumns={true}
                                rowSelection="multiple"
                                suppressRowClickSelection={true}
                                pagination={true}
                                paginationAutoPageSize
                                onSelectionChanged={onCheckboxChange}
                                quickFilterText={searchText}
                                loadingOverlayComponent={LoadingOverlayComponent}
                                noRowsOverlayComponent={(props: any) => <NoRowsOverlayComponent {...props} />}
                                suppressMenuHide={true}
                                suppressContextMenu={true}
                                modules={[GridChartsModule, SetFilterModule]}
                                onFilterChanged={onFilterChanged}
                            />
                        </Box>
                    </Box>
                ) : null}
            </Box>
            {openImportDialog && (
                <ImportContactFlow closeDialog={closeDialog} onUploadSuccess={onUploadSuccess} />
            )}
            {openAddEditDialog ? (
                <AddContactDialog open={openAddEditDialog} onClose={toggleAddEditDialog} handleAddContactSuccess={handleAddContactSuccess} preSelectedCompany={preSelectedCompany} />
            ) : null}
            {openConfirmationDialog && (
                <DeleteConfirmationDialog
                    title={'Delete Contact(s)'}
                    open={openConfirmationDialog}
                    handleDialog={closeConfirmationDialog}
                    confirmAction={() => onDeleteBulk(true)}
                    loading={deleteLoading}
                >
                    <Box width="40vw" my={4}>
                        <BodySmall limit={false} fontWeight="600" color="text.primary">
                            {deleteTitle(deleteIds.length)}
                        </BodySmall>
                        <Info mt={4}>{contactDeleteBody()}</Info>
                    </Box>
                </DeleteConfirmationDialog>
            )}
            {openBulkEditDialog && selectedRows.length ? (
                <BulkEditContactModal
                    selectedContactIds={selectedRows.map(contact => contact.id)}
                    onClose={closeBulkEditDialog}
                    onSuccess={handleBulkEditSuccess}
                />
            ) : null}
        </>
    );
};

export default ContactsTable;
